import React, { useState, useEffect, useCallback } from "react";
import { Bar, Pie, Doughnut } from "react-chartjs-2";
import api from "../services/api";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend
);



const DashboardRequisiciones = () => {
  // Estado consolidado para mejor manejo
  const [data, setData] = useState({
    resumen: {},
    mensual: [],
    departamentos: [],
    actividad: [],
    topMateriales: [],
    kpi: {},
  });
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Función memoizada para cargar datos
  const cargarDatos = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Realizar todas las llamadas en paralelo para mejor rendimiento
      const [r1, r2, r3, r4, r5, r6] = await Promise.all([
        api.get("/dashboard/requisiciones/resumen"),
        api.get("/dashboard/requisiciones/mensual"),
        api.get("/dashboard/requisiciones/departamentos"),
        api.get("/dashboard/requisiciones/actividad"),
        api.get("/dashboard/requisiciones/top-materiales"),
        api.get("/dashboard/requisiciones/kpi"),
      ]);

      setData({
        resumen: r1.data || {},
        mensual: r2.data || [],
        departamentos: r3.data || [],
        actividad: r4.data || [],
        topMateriales: r5.data || [],
        kpi: r6.data || {},
      });
    } catch (error) {
      console.error("Error cargando dashboard de requisiciones:", error);
      setError("No se pudieron cargar los datos. Por favor, intente nuevamente.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  // Estilos mejorados con variables CSS
  const styles = {
    container: {
      padding: "20px",
      maxWidth: "1400px",
      margin: "0 auto",
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
    },
    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "25px",
    },
    titulo: {
      fontSize: "28px",
      fontWeight: "bold",
      margin: 0,
      color: "#333",
    },
    btnRefresh: {
      padding: "10px 20px",
      backgroundColor: "#0275d8",
      color: "white",
      border: "none",
      borderRadius: "6px",
      cursor: "pointer",
      fontSize: "14px",
      fontWeight: "500",
      transition: "background-color 0.2s",
    },
    tarjetas: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
      gap: "20px",
      marginBottom: "30px",
    },
    card: {
      background: "#fff",
      padding: "24px",
      borderRadius: "12px",
      boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
      textAlign: "center",
      transition: "transform 0.2s, box-shadow 0.2s",
      border: "1px solid #f0f0f0",
    },
    cardTitulo: {
      fontSize: "14px",
      fontWeight: "600",
      marginBottom: "12px",
      color: "#666",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    },
    valor: {
      fontSize: "36px",
      color: "#0275d8",
      fontWeight: "bold",
      margin: 0,
    },
    valorTexto: {
      fontSize: "18px",
      color: "#0275d8",
      fontWeight: "600",
      margin: 0,
    },
    seccion: {
      marginTop: "30px",
      marginBottom: "30px",
      background: "#fff",
      padding: "24px",
      borderRadius: "12px",
      boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
      border: "1px solid #f0f0f0",
    },
    seccionTitulo: {
      fontSize: "20px",
      fontWeight: "bold",
      marginBottom: "20px",
      color: "#333",
      borderBottom: "2px solid #0275d8",
      paddingBottom: "10px",
    },
    timeline: {
      background: "#fff",
      padding: "24px",
      borderRadius: "12px",
      boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
      border: "1px solid #f0f0f0",
      marginTop: "30px",
    },
    timelineItem: {
      padding: "12px",
      borderLeft: "3px solid #0275d8",
      marginBottom: "10px",
      paddingLeft: "16px",
      backgroundColor: "#f8f9fa",
      borderRadius: "4px",
    },
    loading: {
      textAlign: "center",
      padding: "60px",
      fontSize: "18px",
      color: "#666",
    },
    error: {
      textAlign: "center",
      padding: "40px",
      backgroundColor: "#f8d7da",
      color: "#721c24",
      borderRadius: "8px",
      marginTop: "20px",
    },
    chartContainer: {
      maxHeight: "400px",
      position: "relative",
    },
    kpiGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
      gap: "20px",
      marginTop: "20px",
    },
    emptyState: {
      textAlign: "center",
      padding: "40px",
      color: "#999",
      fontStyle: "italic",
    },
  };

  // Componente de carga
  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>
          <div style={{ fontSize: "48px", marginBottom: "20px" }}>⏳</div>
          <div>Cargando datos del dashboard...</div>
        </div>
      </div>
    );
  }

  // Componente de error
  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.error}>
          <div style={{ fontSize: "48px", marginBottom: "20px" }}>⚠️</div>
          <div>{error}</div>
          <button
            style={{ ...styles.btnRefresh, marginTop: "20px" }}
            onClick={cargarDatos}
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  const { resumen, mensual, departamentos, actividad, topMateriales, kpi } = data;

  // Configuración común de gráficos
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          padding: 15,
          font: {
            size: 12,
          },
        },
      },
    },
  };

  return (
    <div style={styles.container}>
      {/* Header con botón de refresh */}
      <div style={styles.header}>
        <h1 style={styles.titulo}>📊 Dashboard de Requisiciones</h1>
        <button
          style={styles.btnRefresh}
          onClick={cargarDatos}
          onMouseEnter={(e) => (e.target.style.backgroundColor = "#025aa5")}
          onMouseLeave={(e) => (e.target.style.backgroundColor = "#0275d8")}
        >
          🔄 Actualizar
        </button>
      </div>

      {/* TARJETAS RESUMEN */}
      <div style={styles.tarjetas}>
        <div style={styles.card}>
          <div style={styles.cardTitulo}>Total</div>
          <div style={styles.valor}>{resumen.total || 0}</div>
        </div>

        <div style={styles.card}>
          <div style={styles.cardTitulo}>Aprobadas</div>
          <div style={{ ...styles.valor, color: "#28a745" }}>
            {resumen.aprobadas || 0}
          </div>
          {resumen.total > 0 && (
            <div style={{ fontSize: "12px", color: "#666", marginTop: "8px" }}>
              {((resumen.aprobadas / resumen.total) * 100).toFixed(1)}%
            </div>
          )}
        </div>

        <div style={styles.card}>
          <div style={styles.cardTitulo}>Rechazadas</div>
          <div style={{ ...styles.valor, color: "#dc3545" }}>
            {resumen.rechazadas || 0}
          </div>
          {resumen.total > 0 && (
            <div style={{ fontSize: "12px", color: "#666", marginTop: "8px" }}>
              {((resumen.rechazadas / resumen.total) * 100).toFixed(1)}%
            </div>
          )}
        </div>

        <div style={styles.card}>
          <div style={styles.cardTitulo}>Pendientes</div>
          <div style={{ ...styles.valor, color: "#ffc107" }}>
            {resumen.pendientes || 0}
          </div>
          {resumen.total > 0 && (
            <div style={{ fontSize: "12px", color: "#666", marginTop: "8px" }}>
              {((resumen.pendientes / resumen.total) * 100).toFixed(1)}%
            </div>
          )}
        </div>

        {resumen.departamento_top && (
          <div style={styles.card}>
            <div style={styles.cardTitulo}>Dept. más activo</div>
            <div style={styles.valorTexto}>
              {resumen.departamento_top.nombre}
            </div>
            {resumen.departamento_top.cantidad && (
              <div style={{ fontSize: "12px", color: "#666", marginTop: "8px" }}>
                {resumen.departamento_top.cantidad} solicitudes
              </div>
            )}
          </div>
        )}

        {resumen.material_top && (
          <div style={styles.card}>
            <div style={styles.cardTitulo}>Material más solicitado</div>
            <div style={styles.valorTexto}>{resumen.material_top.nombre}</div>
            {resumen.material_top.cantidad && (
              <div style={{ fontSize: "12px", color: "#666", marginTop: "8px" }}>
                {resumen.material_top.cantidad} veces
              </div>
            )}
          </div>
        )}
      </div>

      {/* GRÁFICO POR MES */}
      {mensual.length > 0 ? (
        <div style={styles.seccion}>
          <h2 style={styles.seccionTitulo}>📅 Solicitudes por mes</h2>
          <div style={styles.chartContainer}>
            <Bar
              data={{
                labels: mensual.map((m) => m.mes),
                datasets: [
                  {
                    label: "Cantidad de solicitudes",
                    data: mensual.map((m) => m.cantidad),
                    backgroundColor: "rgba(2, 117, 216, 0.8)",
                    borderColor: "#0275d8",
                    borderWidth: 1,
                  },
                ],
              }}
              options={{
                ...chartOptions,
                scales: {
                  y: {
                    beginAtZero: true,
                    ticks: {
                      precision: 0,
                    },
                  },
                },
              }}
            />
          </div>
        </div>
      ) : (
        <div style={styles.seccion}>
          <h2 style={styles.seccionTitulo}>📅 Solicitudes por mes</h2>
          <div style={styles.emptyState}>No hay datos disponibles</div>
        </div>
      )}

      {/* GRÁFICO DEPARTAMENTOS */}
      {departamentos.length > 0 ? (
        <div style={styles.seccion}>
          <h2 style={styles.seccionTitulo}>🏢 Solicitudes por departamento</h2>
          <div style={styles.chartContainer}>
            <Pie
              data={{
                labels: departamentos.map((d) => d.nombre || `Dept ID ${d.departamento_id}`),
                datasets: [
                  {
                    data: departamentos.map((d) => d.cantidad),
                    backgroundColor: [
                      "#0275d8",
                      "#28a745",
                      "#dc3545",
                      "#ffc107",
                      "#17a2b8",
                      "#6610f2",
                    ],
                    borderWidth: 2,
                    borderColor: "#fff",
                  },
                ],
              }}
              options={chartOptions}
            />
          </div>
        </div>
      ) : (
        <div style={styles.seccion}>
          <h2 style={styles.seccionTitulo}>🏢 Solicitudes por departamento</h2>
          <div style={styles.emptyState}>No hay datos disponibles</div>
        </div>
      )}

      {/* TOP MATERIALES */}
      {topMateriales.length > 0 ? (
        <div style={styles.seccion}>
          <h2 style={styles.seccionTitulo}>📦 Materiales más usados</h2>
          <div style={styles.chartContainer}>
            <Doughnut
              data={{
                labels: topMateriales.map(
                  (m) => m.nombre || `Material ${m.MaterialId}`
                ),
                datasets: [
                  {
                    data: topMateriales.map((m) => m.total),
                    backgroundColor: [
                      "#0275d8",
                      "#5bc0de",
                      "#28a745",
                      "#ffc107",
                      "#dc3545",
                    ],
                    borderWidth: 2,
                    borderColor: "#fff",
                  },
                ],
              }}
              options={chartOptions}
            />
          </div>
        </div>
      ) : (
        <div style={styles.seccion}>
          <h2 style={styles.seccionTitulo}>📦 Materiales más usados</h2>
          <div style={styles.emptyState}>No hay datos disponibles</div>
        </div>
      )}

      {/* ACTIVIDAD RECIENTE */}
      {actividad.length > 0 ? (
        <div style={styles.timeline}>
          <h2 style={styles.seccionTitulo}>🕒 Actividad reciente</h2>
          {actividad.map((a, i) => (
            <div key={i} style={styles.timelineItem}>
              <strong style={{ color: "#0275d8" }}>{a.hora}</strong> — {a.evento}
            </div>
          ))}
        </div>
      ) : (
        <div style={styles.timeline}>
          <h2 style={styles.seccionTitulo}>🕒 Actividad reciente</h2>
          <div style={styles.emptyState}>No hay actividad reciente</div>
        </div>
      )}

      {/* KPI AVANZADOS */}
      <div style={styles.seccion}>
        <h2 style={styles.seccionTitulo}>📌 KPI Avanzados</h2>
        <div style={styles.kpiGrid}>
          <div style={styles.card}>
            <div style={styles.cardTitulo}>⏳ Tiempo promedio de aprobación</div>
            <div style={styles.valor}>
              {kpi.tiempo_promedio_aprobacion || "N/A"}
            </div>
          </div>

          <div style={styles.card}>
            <div style={styles.cardTitulo}>⚠️ Materiales críticos</div>
            <div style={styles.valor}>
              {kpi.materiales_criticos?.length ?? 0}
            </div>
            <div style={{ fontSize: "14px", color: "#666", marginTop: "8px" }}>
              materiales en nivel crítico
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardRequisiciones;